/**
 * Subscription tier helper utilities
 * Determines which features are available based on subscription tier
 */

const TIER_HIERARCHY = {
    'free': 0,
    'starter': 1,
    'pro': 2,
    'ultra': 3,
    'enterprise': 4
};

export const TEAM_LIMITS = {
    'free': 0,
    'starter': 3,
    'pro': 10,
    'ultra': 50,
    'enterprise': 999
};


/**
 * Normalize tier name to lowercase for comparison
 */
export const normalizeTier = (tier) => {
    if (!tier) return 'free';
    return String(tier).toLowerCase();
};

/**
 * Check if a feature is available for the given subscription tier
 * @param {string} userTier - User's subscription tier (e.g., 'starter', 'pro')
 * @param {string} requiredTier - Minimum tier required for feature
 * @returns {boolean}
 */
export const hasFeatureAccess = (userTier, requiredTier) => {
    const normalizedUserTier = normalizeTier(userTier);
    const normalizedRequiredTier = normalizeTier(requiredTier);

    const userLevel = TIER_HIERARCHY[normalizedUserTier] ?? 0;
    const requiredLevel = TIER_HIERARCHY[normalizedRequiredTier] ?? 0;

    return userLevel >= requiredLevel;
};

/**
 * Get feature availability for a subscription
 * @param {object} subscription - Subscription data from API
 * @returns {object} Feature flags
 */
export const getFeatureFlags = (subscription) => {
    // Extract tier from various possible locations in the API response
    // Priority: subscription.tier > subscription.plan.tier > subscription.plan.name
    let tier = subscription?.tier || subscription?.plan?.tier || subscription?.plan?.name;

    // Log warning if no tier found in backend response
    if (!tier) {
        console.warn("⚠️ WARNING: No tier found in subscription data from backend!");
        console.warn("⚠️ Subscription object:", subscription);
        console.warn("⚠️ Defaulting to 'free' tier - this may be incorrect!");
        tier = 'free';
    }

    // Normalize tier name (e.g., "Starter" -> "starter", "Pro" -> "pro")
    tier = normalizeTier(tier);

    console.log("✅ Extracted tier for feature flags:", tier, "from subscription:", subscription);

    return {
        // Collaboration features (Starter+)
        hasCollaboration: hasFeatureAccess(tier, 'starter'),

        // Team Chat (all tiers)
        hasTeamChat: true,

        // Teams management (Starter+)
        hasTeams: hasFeatureAccess(tier, 'starter'),

        // Case Assistant - formerly "chatbot" (all tiers)
        hasCaseAssistant: true,

        // Notes feature (all tiers)
        hasNotes: true,

        // Meeting Assistant (all tiers)
        hasMeetingAssistant: true,

        // Calendar (Ultra+ only)
        hasCalendar: hasFeatureAccess(tier, 'ultra'),

        // AMA Assistant (Ultra+ only)
        hasAMA: hasFeatureAccess(tier, 'ultra'),

        // Executive Dashboard (all tiers)
        hasExecutiveDashboard: true,

        // P&L Tracker (all tiers)
        hasPnlTracker: true,

        // Case Management (all tiers)
        hasCaseManagement: true,

        // Advanced reports (Starter+)
        hasAdvancedReports: hasFeatureAccess(tier, 'starter'),

        // Priority support (Pro+)
        hasPrioritySupport: hasFeatureAccess(tier, 'pro'),

        // API Access (Ultra+ only)
        hasApiAccess: hasFeatureAccess(tier, 'ultra'),

        // Ultra tier check
        isUltra: hasFeatureAccess(tier, 'ultra'),

        // Ultra or Enterprise features
        isUltraOrEnterprise: hasFeatureAccess(tier, 'ultra'),

        // Enterprise-only features
        isEnterprise: hasFeatureAccess(tier, 'enterprise'),

        // Social Media Management (Ultra+ only)
        hasSocialMedia: hasFeatureAccess(tier, 'ultra'),
    };
};

/**
 * Get upgrade message for a locked feature
 * @param {string} featureName - Name of the feature
 * @param {string} requiredTier - Minimum tier required
 * @returns {string}
 */
export const getUpgradeMessage = (featureName, requiredTier = 'starter') => {
    const tierName = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1);
    return `${featureName} is available on ${tierName} plan and above. Upgrade to unlock this feature.`;
};

/**
 * Get team member limit for a tier
 */
export const getTeamLimit = (tier) => {
    const normalized = normalizeTier(tier);
    return TEAM_LIMITS[normalized] ?? 0;
};

export const SOCIAL_MEDIA_LIMITS = {
    'free': 0,
    'starter': 0,
    'pro': 0,
    'ultra': 5,
    'enterprise': 25
};

/**
 * Get social media integration limit for a tier
 */
export const getSocialMediaLimit = (tier) => {
    const normalized = normalizeTier(tier);
    return SOCIAL_MEDIA_LIMITS[normalized] ?? 0;
};
